/**
 * ContentSelectionModal - Modal for selecting specific content items for schema processing
 *
 * Coordinates all child components for the selective schema application workflow:
 * - ContentFilterCard: Search and filter controls
 * - ContentSelectionTable: Table with checkboxes for item selection
 * - SelectionSummary: Shows selection count and action button
 * - ContentProgressPanel: Real-time progress during schema application
 */

import { Modal } from "antd";
import type React from "react";
import { useState } from "react";
import type { FAQContentType } from "@/types/schema";
import { useContentSelectionModal } from "@/hooks/useContentSelectionModal";
import {
	useContentListing,
	useApplyArticleSchemaSelective,
	useApplyFAQSchemaSelective,
} from "@/hooks/useSchemaManagement";
import { FixingOverlay } from "@/components/ui/FixingLoader";
import { ContentFilterCard } from "./ContentFilterCard";
import { ContentSelectionTable } from "./ContentSelectionTable";
import { SelectionSummary } from "./SelectionSummary";
import { ContentProgressPanel } from "./ContentProgressPanel";

interface ContentSelectionModalProps {
	isOpen: boolean;
	onClose: () => void;
	schemaType: "article" | "faq";
	contentType?: FAQContentType;
}

export const ContentSelectionModal: React.FC<ContentSelectionModalProps> = ({
	isOpen,
	onClose,
	schemaType,
	contentType,
}) => {
	const [isProcessing, setIsProcessing] = useState(false);
	const [progress, setProgress] = useState({
		processed: 0,
		total: 0,
		status: "idle" as "idle" | "running" | "completed" | "failed",
		errorMessage: "",
	});

	// Initialize modal state with empty array - content will come from query
	const modalState = useContentSelectionModal([], {});

	// Fetch content listing
	const { data: contentData, isLoading: isLoadingContent } = useContentListing(
		modalState.filters,
		1, // page
		50, // pageSize
		isOpen, // enabled - only fetch when modal is open
	);

	// Selective mutation hooks
	const applyArticleSelective = useApplyArticleSchemaSelective();
	const applyFAQSelective = useApplyFAQSchemaSelective();

	const handleApplySchema = async () => {
		if (modalState.selectedIds.length === 0) return;

		setIsProcessing(true);
		setProgress({
			processed: 0,
			total: modalState.selectedIds.length,
			status: "running",
			errorMessage: "",
		});

		try {
			if (schemaType === "article") {
				await applyArticleSelective.mutateAsync(modalState.selectedIds);
			} else {
				await applyFAQSelective.mutateAsync({
					contentType: contentType || "post",
					itemIds: modalState.selectedIds,
				});
			}

			setProgress((prev) => ({
				...prev,
				processed: modalState.selectedIds.length,
				status: "completed",
			}));
		} catch (error) {
			setProgress((prev) => ({
				...prev,
				status: "failed",
				errorMessage: error instanceof Error ? error.message : "Unknown error",
			}));
		} finally {
			setIsProcessing(false);
		}
	};

	const handleClose = () => {
		if (!isProcessing) {
			modalState.clearSelection();
			setProgress({
				processed: 0,
				total: 0,
				status: "idle",
				errorMessage: "",
			});
			onClose();
		}
	};

	return (
		<Modal
			title={`Select Content for ${schemaType === "article" ? "Article" : "FAQ"} Schema`}
			open={isOpen}
			onCancel={handleClose}
			width={900}
			footer={null}
			destroyOnHidden
			maskClosable={!isProcessing}
			closable={!isProcessing}
		>
			<div className="relative space-y-4">
				<FixingOverlay
					show={
						applyArticleSelective.isPending || applyFAQSelective.isPending
					}
					label="Applying schema…"
				/>
				{/* Filters and bulk selection controls */}
				<ContentFilterCard
					schemaType={schemaType}
					contentType={contentType}
					filters={modalState.filters}
					onFiltersChange={modalState.setFilters}
					selectedCount={modalState.selectedIds.length}
					onSelectPage={() =>
						modalState.selectAllPage(contentData?.items ?? [])
					}
					onSelectAll={() =>
						modalState.selectAllFiltered(contentData?.items ?? [])
					}
					onClearSelection={() => modalState.clearSelection()}
				/>

				{/* Content items table with checkboxes */}
				<ContentSelectionTable
					schemaType={schemaType}
					contentType={contentType}
					selectedIds={modalState.selectedIds}
					onSelectionChange={modalState.setSelectedIds}
					filters={modalState.filters}
					items={contentData?.items ?? []}
					loading={isLoadingContent}
				/>

				{/* Selection summary and action button */}
				{!isProcessing && (
					<SelectionSummary
						schemaType={schemaType}
						selectedCount={modalState.selectedIds.length}
						totalCount={contentData?.total_count ?? 0}
						onApply={handleApplySchema}
						disabled={modalState.selectedIds.length === 0}
					/>
				)}

				{/* Progress panel during processing */}
				{isProcessing && (
					<ContentProgressPanel
						processed={progress.processed}
						total={progress.total}
						status={progress.status}
						errorMessage={progress.errorMessage}
					/>
				)}
			</div>
		</Modal>
	);
};

export default ContentSelectionModal;
